import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import AppDocTabs, { AppNotFound } from "../components/AppDocTabs";
import { getApp } from "../data/apps";
import { SITE, mailto } from "../data/site";
import "../styles/docs.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function DeleteAccountPage() {
  const { slug = "" } = useParams();
  const app = getApp(slug);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (app) document.title = `${app.name} 계정 삭제 | kwangbok.me`;
  }, [app]);

  if (!app) return <AppNotFound slug={slug} />;
  const subject = `[${app.name}] 계정 삭제 요청`;

  if (!app.has_account) {
    return (
      <SiteLayout crumb={`apps / ${app.slug} / delete-account`}>
        <div className="docs">
          <AppDocTabs app={app} current="delete-account" />
          <div className="docs-card docs-pad">
            <h1 className="docs-h1">계정과 데이터 삭제</h1>
            <p>이 앱은 계정을 만들지 않아 삭제할 계정이 없습니다.</p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const d = app.deletion ?? { in_app_steps: [], deleted: [], retained: [] };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim();
    const msg = !v ? "가입 이메일을 입력해 주세요." : EMAIL_RE.test(v) ? "" : "이메일 형식이 올바르지 않습니다. 예: name@example.com";
    setError(msg);
    if (msg) {
      emailRef.current?.focus();
      return;
    }
    // 정적 사이트라 서버가 없다. 메일 앱으로 요청을 넘긴다 (D-13)
    window.location.href = mailto(subject, `가입 이메일: ${v}\n삭제 사유: ${reason.trim() || "없음"}\n`, app.operator.email);
    setSent(true);
  };

  return (
    <SiteLayout crumb={`apps / ${app.slug} / delete-account`}>
      <div className="docs">
        <AppDocTabs app={app} current="delete-account" />
        <div className="docs-head">
          <h1 className="docs-h1 docs-h1--lg">계정과 데이터 삭제</h1>
          <p className="docs-muted">요청을 받으면 {SITE.deletion_days}일 안에 처리하고 메일로 알려 드립니다.</p>
        </div>

        <div className="docs-split docs-split--half">
          <section className="docs-card docs-pad docs-method" aria-labelledby="docs-m1">
            <span className="docs-num">method 01</span>
            <h2 id="docs-m1">앱에서 바로 삭제</h2>
            <p className="docs-muted">로그인할 수 있으면 앱 안에서 즉시 삭제됩니다.</p>
            <ol className="docs-steps">
              {d.in_app_steps.map((s, i) => (
                <li key={i}>
                  <span className="docs-num" aria-hidden="true">
                    step {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </section>

          <section className="docs-card docs-pad docs-method" aria-labelledby="docs-m2">
            <span className="docs-num">method 02</span>
            <h2 id="docs-m2">웹에서 삭제 요청</h2>
            {sent ? (
              <div role="status" className="docs-sent">
                <p>
                  메일 앱에서 요청 메일을 보내 주세요. 접수 후 {SITE.deletion_days}일 이내에 처리하고 결과를 메일로
                  알려 드립니다.
                </p>
                <p className="docs-muted">메일 앱이 열리지 않으면 아래 주소를 복사해 같은 내용으로 직접 보내 주세요.</p>
                <dl className="docs-sent-info">
                  <dt className="docs-mono docs-muted">받는 주소</dt>
                  <dd>{app.operator.email}</dd>
                  <dt className="docs-mono docs-muted">제목</dt>
                  <dd>{subject}</dd>
                </dl>
              </div>
            ) : (
              <form className="docs-form" noValidate onSubmit={onSubmit}>
                <p className="docs-muted">앱을 지웠거나 로그인할 수 없을 때 쓰세요.</p>
                <label htmlFor="docs-email">가입 이메일, 필수</label>
                <input
                  id="docs-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? "docs-email-error" : undefined}
                />
                {error && (
                  <p id="docs-email-error" className="docs-error">
                    {error}
                  </p>
                )}
                <label htmlFor="docs-reason">삭제 사유, 선택</label>
                <textarea id="docs-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                <button type="submit" className="docs-btn">
                  삭제 요청 보내기
                </button>
              </form>
            )}
          </section>
        </div>

        {(d.deleted.length > 0 || d.retained.length > 0) && (
          <div className="docs-card">
            <table className="docs-table docs-table--stack">
              <caption className="docs-sr">삭제되는 데이터와 보관하는 데이터</caption>
              <thead>
                <tr>
                  <th scope="col">type</th>
                  <th scope="col">data</th>
                  <th scope="col">when</th>
                  <th scope="col">basis</th>
                </tr>
              </thead>
              <tbody>
                {d.deleted.map((r) => (
                  <tr key={`d-${r.item}`}>
                    <th scope="row">삭제</th>
                    <td data-label="data">{r.item}</td>
                    <td data-label="when">{r.when}</td>
                    <td data-label="basis" className="docs-muted">
                      해당 없음
                    </td>
                  </tr>
                ))}
                {d.retained.map((r) => (
                  <tr key={`r-${r.item}`}>
                    <th scope="row">보관</th>
                    <td data-label="data">{r.item}</td>
                    <td data-label="when">{r.period}</td>
                    <td data-label="basis">{r.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
